/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable default-param-last */
import {
  AgencyRateStatement,
  ChargeStatement,
  CreateStatementInput,
  CreateStatementMutationVariables,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  DeleteStatementMutationVariables,
  type Communication,
  type File as FileModel,
  getFile,
  getSupportingDocumentFiles,
  getSupportingDocumentsSource,
  LooseObject,
  SupportingDocumentsSource,
  Posting,
  PostingChargeType,
  PostingStatus,
  RecalculateStatementMutationVariables,
  RepaymentStatement,
  Statement,
  StatementStatus,
  UpdateStatementInput,
  cleanInputCreate,
  cleanInputUpdate,
  createStatement as createMutation,
  deleteStatement as deleteMutation,
  getReadId,
  getStatement as getStatementQuery,
  getTableClientId,
  recalculateStatement as recalculateStatementMutation,
  removeNestedFieldsExcept,
  syncAgencyRateStatements as syncAgencyRateStatementsQuery,
  syncChargeStatements,
  syncPostings as syncPostingsQuery,
  syncRepaymentStatements as syncRepaymentStatementsQuery,
  syncStatements,
  updateStatement as updateMutation,
} from '@rentguru/commons-utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteAndHideEntityWithFetchBefore, get, list, mutation } from '@up2rent/graphql';
import compact from 'lodash/compact';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import { POSTINGS_FIELDS_TO_SYNC } from './PostingsContext';
import { useUser } from './UserContext';
import { getStatementAttachSupportingDocuments, syncChargeStatementsWithDocuments } from './utils/cleanedQueries';

export interface StatementContext {
  statementsLoading: boolean;
  /** Loading with no statements read yet, unlike `statementsLoading` that a background refetch sets too. */
  statementsFirstLoading: boolean;
  statementsFetched: boolean;
  statementsError: string | null;
  statements: Statement[];
  tenantStatementsToCheck: Statement[];
  tenantStatementsArchived: Statement[];
  ownerStatementsToCheck: Statement[];
  ownerStatementsArchived: Statement[];
  getStatement: (id: string) => Statement | undefined;
  createStatement: (input: Omit<CreateStatementInput, 'clientId' | 'readId'>) => Promise<Statement>;
  updateStatement: (updates: UpdateStatementInput) => Promise<Statement>;
  deleteStatement: (statement: Statement) => Promise<Statement>;
  recalculateStatement: (id: string) => Promise<boolean>;
}

export type StatementMutations = Pick<StatementContext, 'recalculateStatement' | 'updateStatement' | 'deleteStatement'>;

const STATEMENT_RELATIONS_TO_REMOVE = ['agencyRateStatements', 'chargeStatements', 'repaymentStatements'];
const STATEMENT_NESTED_FIELDS_TO_KEEP = ['amounts', 'lease', 'owner'];

const STATEMENTS_QUERY = removeNestedFieldsExcept(
  syncStatements,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  STATEMENT_RELATIONS_TO_REMOVE,
  STATEMENT_NESTED_FIELDS_TO_KEEP
);

export const fetchStatements = async (
  by: 'byClientId' | 'byOwnerId' | 'byLeaseId',
  byValue: string,
  additionalFilter?: LooseObject
): Promise<Statement[]> => {
  const indexName = by === 'byClientId' ? 'clientId' : by === 'byOwnerId' ? 'ownerId' : 'leaseId';
  return await list<Statement>(STATEMENTS_QUERY, indexName, byValue, additionalFilter);
};

export const fetchChargeStatements = async (
  by: 'byClientId' | 'byStatement' | 'byCharge',
  byValue: string,
  additionalFilter?: LooseObject
): Promise<ChargeStatement[]> => {
  const indexName = by === 'byClientId' ? 'clientId' : by === 'byStatement' ? 'statementId' : 'chargeId';
  return await list<ChargeStatement>(syncChargeStatements, indexName, byValue, additionalFilter);
};

export const fetchChargeStatementsWithDocuments = async (statementId: string): Promise<ChargeStatement[]> =>
  await list<ChargeStatement>(syncChargeStatementsWithDocuments, 'statementId', statementId);

const fetchStatementSupportingDocumentFiles = async (statementId: string): Promise<FileModel[]> => {
  const statement = await get<Pick<Statement, 'id' | 'attachSupportingDocuments'>>(
    getStatementAttachSupportingDocuments,
    statementId
  );
  if (isNil(statement) || !statement.attachSupportingDocuments) {
    return [];
  }
  return getSupportingDocumentFiles(await fetchChargeStatementsWithDocuments(statementId));
};

/** Supporting documents of a statement communication: those recorded when it left, else those of its statement. */
export const fetchCommunicationSupportingDocumentFiles = async (
  communication: Pick<Communication, 'statementId' | 'status' | 'supportingDocumentFileIds' | 'type'>
): Promise<FileModel[]> => {
  const statementId = communication.statementId ?? '';
  switch (getSupportingDocumentsSource(communication)) {
    case SupportingDocumentsSource.RECORDED_AT_SEND: {
      const recordedFileIds = communication.supportingDocumentFileIds ?? [];
      return compact(await Promise.all(recordedFileIds.map((fileId) => get<FileModel>(getFile, fileId))));
    }
    case SupportingDocumentsSource.STATEMENT:
      return await fetchStatementSupportingDocumentFiles(statementId);
    case SupportingDocumentsSource.NONE:
    default:
      return [];
  }
};

export const requestStatementRecalculation = async (statementId: string, userId: string, clientId: string) =>
  await mutation<{ status: boolean; id: string }, RecalculateStatementMutationVariables>(recalculateStatementMutation, {
    input: { statementId, userId, clientId },
  });

export const fetchRepaymentStatements = async (
  by: 'byClientId' | 'byStatement',
  byValue: string,
  additionalFilter?: LooseObject
): Promise<RepaymentStatement[]> => {
  const indexName = by === 'byClientId' ? 'clientId' : 'statementId';
  return await list<RepaymentStatement>(syncRepaymentStatementsQuery, indexName, byValue, additionalFilter);
};

export const fetchAgencyRateStatements = async (
  by: 'byClientId' | 'byStatement' | 'byAgencyRate',
  byValue: string,
  additionalFilter?: LooseObject
): Promise<AgencyRateStatement[]> => {
  const indexName = by === 'byClientId' ? 'clientId' : by === 'byAgencyRate' ? 'agencyRateId' : 'statementId';
  return await list<AgencyRateStatement>(syncAgencyRateStatementsQuery, indexName, byValue, additionalFilter);
};

export const getAvailableTenantPostingsForLease = async (statementLeaseId: string, periodTo: string) => {
  const additionalFilter = {
    status: { eq: PostingStatus.TO_BE_PAID_BY_TENANT },
    chargeId: { attributeExists: true },
    statementId: { attributeExists: false },
    periodFrom: { le: periodTo },
    chargeType: { eq: PostingChargeType.TENANT },
  };

  return await list<Posting>(
    removeNestedFieldsExcept(
      syncPostingsQuery,
      DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
      ['vatRate'],
      POSTINGS_FIELDS_TO_SYNC
    ),
    'leaseId',
    statementLeaseId,
    additionalFilter
  );
};

const classifyStatements = (statements: Statement[]) => {
  return statements.reduce(
    (
      acc: {
        tenantStatementsToCheck: Statement[];
        tenantStatementsArchived: Statement[];
        ownerStatementsToCheck: Statement[];
        ownerStatementsArchived: Statement[];
      },
      statement
    ) => {
      if (statement.lease) {
        // Tenant statement
        if (statement.status === StatementStatus.TO_VALIDATE) {
          acc.tenantStatementsToCheck.push(statement);
        } else {
          acc.tenantStatementsArchived.push(statement);
        }
      } else if (statement.owner) {
        // Owner statmeent
        if (statement.status === StatementStatus.TO_VALIDATE) {
          acc.ownerStatementsToCheck.push(statement);
        } else {
          acc.ownerStatementsArchived.push(statement);
        }
      }
      return acc;
    },
    {
      tenantStatementsToCheck: [],
      tenantStatementsArchived: [],
      ownerStatementsToCheck: [],
      ownerStatementsArchived: [],
    }
  );
};

export const clientStatementsQueryKey = (clientId: string | null | undefined) =>
  ['statements', clientId ?? null] as const;

export const statementsQueryKey = (
  clientId: string | null | undefined,
  by: 'byClientId' | 'byLeaseId' | 'byOwnerId',
  byValue?: string
) => [...clientStatementsQueryKey(clientId), by, byValue ?? null] as const;

export const useStatements = (
  shouldFetchStatement: boolean = false,
  by: 'byClientId' | 'byLeaseId' | 'byOwnerId' = 'byClientId',
  byValue?: string
): StatementContext => {
  const queryClient = useQueryClient();
  const { clientId, userId } = useUser();

  const queryKey = statementsQueryKey(clientId, by, byValue);

  const { data, isFetching, isLoading, isPending, error } = useQuery<Statement[]>({
    queryKey,
    queryFn: async () => {
      if (isNil(clientId)) {
        return [];
      }
      return fetchStatements(by, byValue ?? getTableClientId(clientId, 'Statement'));
    },
    enabled: shouldFetchStatement && !isNil(clientId),
  });

  const statements = shouldFetchStatement ? (data ?? []) : [];
  const statementsLoading = shouldFetchStatement && isFetching;
  const statementsFirstLoading = shouldFetchStatement && isLoading;
  const statementsFetched = shouldFetchStatement && !isPending;
  const statementsError = shouldFetchStatement ? (error?.message ?? null) : null;

  const setStatements = (updater: (current: Statement[]) => Statement[]) => {
    const cachedStatements = queryClient.getQueryData<Statement[]>(queryKey);
    if (isNil(cachedStatements)) {
      queryClient.invalidateQueries({ queryKey: clientStatementsQueryKey(clientId) });
      return;
    }
    queryClient.setQueryData<Statement[]>(queryKey, updater(cachedStatements));
    queryClient.invalidateQueries({
      queryKey: clientStatementsQueryKey(clientId),
      predicate: (query) => !isEqual(query.queryKey, queryKey),
    });
  };

  const { tenantStatementsToCheck, tenantStatementsArchived, ownerStatementsToCheck, ownerStatementsArchived } =
    classifyStatements(statements);

  const getStatement = (id: string): Statement | undefined => {
    return statements.find((statement) => statement.id === id);
  };

  const createStatement = async (input: Omit<CreateStatementInput, 'clientId' | 'readId'>): Promise<Statement> => {
    const statement = await mutation<Statement, CreateStatementMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateStatementInput),
        readId: getReadId(clientId!, 'Statement'),
        clientId: getTableClientId(clientId!, 'Statement'),
      },
    });
    setStatements((current) => [...current, statement]);
    return statement;
  };

  const updateStatement = async (updates: UpdateStatementInput): Promise<Statement> => {
    const result = await mutation<Statement>(updateMutation, {
      input: cleanInputUpdate(updates, false),
    });
    setStatements((current) =>
      current.map((statement) => {
        if (statement.id === result.id) return result;
        return statement;
      })
    );
    return result;
  };

  const deleteStatement = async (statement: Statement): Promise<Statement> => {
    const result = await deleteAndHideEntityWithFetchBefore<Statement, DeleteStatementMutationVariables>(
      statement,
      getStatementQuery,
      deleteMutation
    );
    setStatements((current) => current.filter((candidate) => candidate.id !== statement.id));
    return result;
  };

  const recalculateStatement = async (id: string) => {
    const result = await requestStatementRecalculation(id, userId!, clientId!);
    if (result.status) {
      const updatedStatement = await get<Statement>(getStatementQuery, id);
      setStatements((current) =>
        current.map((statement) => {
          if (statement.id === updatedStatement.id) return updatedStatement;
          return statement;
        })
      );
    }
    return result.status;
  };

  return {
    statementsLoading,
    statementsFirstLoading,
    statementsFetched,
    statementsError,
    statements,
    tenantStatementsToCheck,
    tenantStatementsArchived,
    ownerStatementsToCheck,
    ownerStatementsArchived,
    getStatement,
    createStatement,
    updateStatement,
    deleteStatement,
    recalculateStatement,
  };
};
